import React, { useEffect, useState } from 'react';
import {
  View, Text, FlatList, TouchableOpacity, StyleSheet, ActivityIndicator,
} from 'react-native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import type { RunStackParamList } from '../../navigation/AppNavigator';
import { runsApi, presetRunsApi } from '../../api';
import { usePresetRunsStore } from '../../store';

type Props = NativeStackScreenProps<RunStackParamList, 'Runs'>;

export default function RunsScreen({ navigation }: Props) {
  const [loading, setLoading] = useState(true);
  const { presetRuns, publicPresetRuns, setPresetRuns, setPublicPresetRuns } = usePresetRunsStore();

  useEffect(() => {
    (async () => {
      try {
        const [mine, pub] = await Promise.all([
          presetRunsApi.list(),
          presetRunsApi.listPublic(),
        ]);
        setPresetRuns(mine);
        setPublicPresetRuns(pub);
      } catch (e) {
        console.warn('Failed to load preset runs', e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <ActivityIndicator style={{ flex: 1 }} size="large" color="#1a73e8" />;

  return (
    <View style={styles.container}>
      {/* Quick actions */}
      <View style={styles.actions}>
        <TouchableOpacity
          style={styles.bigButton}
          onPress={() => navigation.navigate('ActiveRun', { mode: 'free' })}
          testID="free-run-button"
        >
          <Text style={styles.bigButtonText}>🏃 Free Run</Text>
          <Text style={styles.bigButtonSub}>Pass any landmark for auto-announce</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.bigButton, styles.bigButtonAlt]}
          onPress={() => navigation.navigate('StartRun')}
          testID="preset-run-button"
        >
          <Text style={[styles.bigButtonText, { color: '#1a73e8' }]}>📍 Preset Run</Text>
          <Text style={[styles.bigButtonSub, { color: '#555' }]}>Follow a planned route</Text>
        </TouchableOpacity>
      </View>

      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionTitle}>My Preset Runs</Text>
          <TouchableOpacity onPress={() => navigation.navigate('CreatePresetRun')}>
            <Text style={styles.sectionAction}>+ Create</Text>
          </TouchableOpacity>
        </View>
        <FlatList
          data={presetRuns}
          keyExtractor={(i) => i.presetRunId}
          ListEmptyComponent={<Text style={styles.empty}>No preset runs yet.</Text>}
          renderItem={({ item }) => (
            <TouchableOpacity
              style={styles.item}
              onPress={() => navigation.navigate('ActiveRun', { mode: 'preset', presetRunId: item.presetRunId })}
            >
              <Text style={styles.itemName}>{item.name}</Text>
              <Text style={styles.itemMeta}>{item.landmarkIds.length} landmarks · {item.isPublic ? 'Public' : 'Private'}</Text>
            </TouchableOpacity>
          )}
          ItemSeparatorComponent={() => <View style={styles.sep} />}
          scrollEnabled={false}
        />
      </View>

      <TouchableOpacity style={styles.historyButton} onPress={() => navigation.navigate('RunHistory')}>
        <Text style={styles.historyText}>View Run History →</Text>
      </TouchableOpacity>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f8f9fa' },
  actions: { flexDirection: 'row', padding: 16, gap: 12 },
  bigButton: {
    flex: 1, backgroundColor: '#1a73e8', borderRadius: 12,
    padding: 16, alignItems: 'center',
  },
  bigButtonAlt: { backgroundColor: '#fff', borderWidth: 1, borderColor: '#1a73e8' },
  bigButtonText: { color: '#fff', fontSize: 16, fontWeight: '700' },
  bigButtonSub: { color: 'rgba(255,255,255,0.8)', fontSize: 11, marginTop: 4, textAlign: 'center' },
  section: { backgroundColor: '#fff', marginHorizontal: 16, borderRadius: 12, overflow: 'hidden' },
  sectionHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', padding: 16, borderBottomWidth: 1, borderColor: '#f0f0f0' },
  sectionTitle: { fontWeight: '700', fontSize: 15 },
  sectionAction: { color: '#1a73e8', fontSize: 14 },
  item: { padding: 16 },
  itemName: { fontSize: 15, fontWeight: '600' },
  itemMeta: { fontSize: 12, color: '#888', marginTop: 2 },
  sep: { height: 1, backgroundColor: '#f0f0f0' },
  empty: { padding: 16, color: '#aaa', textAlign: 'center' },
  historyButton: { margin: 16, padding: 14, backgroundColor: '#fff', borderRadius: 12, alignItems: 'center' },
  historyText: { color: '#1a73e8', fontWeight: '600' },
});
